import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { ItemFull } from '@lr/shared';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';

type SortKey = 'newest' | 'price-asc' | 'price-desc';

interface ActiveFilters {
  brands: Set<string>;
  categories: Set<string>;
  occasions: Set<string>;
  sizes: Set<string>;
  conditions: Set<string>;
  sort: SortKey;
}

const initialFilters = (): ActiveFilters => ({
  brands: new Set(),
  categories: new Set(),
  occasions: new Set(),
  sizes: new Set(),
  conditions: new Set(),
  sort: 'newest'
});

// Canonical orderings — used to sort filter options regardless of which
// values happen to be in the catalogue.
const SIZE_ORDER = [
  'XXS',
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  '2XL',
  'XXXL',
  '3XL',
  '4XL',
  '5XL'
];
const CONDITION_ORDER = ['excellent', 'very_good', 'good', 'preloved'];

function sizeRank(s: string): number {
  // Strip whitespace and uppercase letter-sizes; numeric sizes fall back to
  // their numeric value (waist sizes, etc.).
  const v = s.trim();
  const upper = v.toUpperCase();
  const idx = SIZE_ORDER.indexOf(upper);
  if (idx !== -1) return idx;
  const n = parseFloat(v);
  if (!Number.isNaN(n)) return 1000 + n;
  // Unknown labels go to the end, alphabetically.
  return 2000;
}

function conditionRank(c: string): number {
  const idx = CONDITION_ORDER.indexOf(c);
  return idx === -1 ? 999 : idx;
}

const FILTERS_STORAGE_KEY = 'lr-browse-filters';

function loadFilters(): ActiveFilters {
  try {
    const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return initialFilters();
    const parsed = JSON.parse(raw) as {
      brands?: string[];
      categories?: string[];
      occasions?: string[];
      sizes?: string[];
      conditions?: string[];
      sort?: SortKey;
    };
    return {
      brands: new Set(parsed.brands ?? []),
      categories: new Set(parsed.categories ?? []),
      occasions: new Set(parsed.occasions ?? []),
      sizes: new Set(parsed.sizes ?? []),
      conditions: new Set(parsed.conditions ?? []),
      sort: parsed.sort ?? 'newest'
    };
  } catch {
    return initialFilters();
  }
}

function saveFilters(f: ActiveFilters): void {
  try {
    sessionStorage.setItem(
      FILTERS_STORAGE_KEY,
      JSON.stringify({
        brands: [...f.brands],
        categories: [...f.categories],
        occasions: [...f.occasions],
        sizes: [...f.sizes],
        conditions: [...f.conditions],
        sort: f.sort
      })
    );
  } catch {
    // sessionStorage can throw in private mode — best-effort only.
  }
}

// Combined scroll-position + page-count storage so we restore the exact
// view the customer left — including how many "Show more" pages they'd
// already loaded. Without the visibleCount, restoring scrollTop past the
// first page lands past the bottom of the rendered list and silently
// snaps back to 0.
const SCROLL_STORAGE_KEY = 'lr-browse-state';

interface SavedScrollState {
  scrollTop: number;
  windowY: number;
  visibleCount: number;
}

function loadScrollState(pageSize: number): SavedScrollState {
  try {
    const raw = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    if (!raw) return { scrollTop: 0, windowY: 0, visibleCount: pageSize };
    const parsed = JSON.parse(raw) as Partial<SavedScrollState>;
    return {
      scrollTop: Number(parsed.scrollTop) || 0,
      windowY: Number(parsed.windowY) || 0,
      visibleCount: Number(parsed.visibleCount) || pageSize
    };
  } catch {
    return { scrollTop: 0, windowY: 0, visibleCount: pageSize };
  }
}

function saveScrollState(state: SavedScrollState): void {
  try {
    sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage quota / private-mode — best-effort only.
  }
}

export function Browse() {
  const { user } = useAuth();
  const location = useLocation();
  const PAGE_SIZE = 16;
  const [items, setItems] = useState<ItemFull[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ActiveFilters>(loadFilters);

  // Keep the persisted filter set in sync so "Keep browsing" / back from item
  // page lands in the same slice the customer was already looking at.
  useEffect(() => {
    saveFilters(filters);
  }, [filters]);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Hydrate visibleCount + scroll from sessionStorage on first render so
  // the very first paint already renders enough cards to support the
  // restored scroll position.
  const initialSaved = useRef(loadScrollState(PAGE_SIZE));
  const [visibleCount, setVisibleCount] = useState(initialSaved.current.visibleCount);
  const scrollRef = useRef<HTMLDivElement>(null);
  const restoredRef = useRef(false);

  // (visibleCount is reset to PAGE_SIZE by toggle/clearAll directly — see
  // those functions below. We don't watch [filters] for resets because
  // React strict-mode would double-fire it on mount and clobber the
  // restored visibleCount.)

  // Continuously persist scroll + page count so navigation away (back
  // button, link click, refresh) always has the latest snapshot. Uses
  // both the inner .scroll div (mobile) and window (desktop) — only one
  // will be non-zero in any given layout.
  useEffect(() => {
    let frame = 0;
    function persist() {
      const scrollTop = scrollRef.current?.scrollTop ?? 0;
      const windowY = typeof window !== 'undefined' ? window.scrollY : 0;
      saveScrollState({ scrollTop, windowY, visibleCount });
    }
    function onScroll() {
      // Throttle to one write per frame.
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        persist();
      });
    }
    const el = scrollRef.current;
    el?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    // Also persist on pagehide so iOS Safari's bfcache captures it.
    window.addEventListener('pagehide', persist);
    return () => {
      el?.removeEventListener('scroll', onScroll);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', persist);
      if (frame) cancelAnimationFrame(frame);
      // Final write on unmount.
      persist();
    };
  }, [visibleCount]);

  // Restore scroll once items have rendered and enough cards exist to
  // support the saved scrollTop. We retry across a few frames because
  // the lazy-loaded images can shift the grid's height as they load.
  useLayoutEffect(() => {
    if (restoredRef.current || loading || items.length === 0) return;
    const target = initialSaved.current;
    if (target.scrollTop === 0 && target.windowY === 0) {
      restoredRef.current = true;
      return;
    }

    let attempts = 0;
    function tryRestore() {
      attempts++;
      const el = scrollRef.current;
      const maxInner = el ? el.scrollHeight - el.clientHeight : 0;
      const maxWindow =
        document.documentElement.scrollHeight - window.innerHeight;

      const innerTarget = Math.min(target.scrollTop, Math.max(0, maxInner));
      const windowTarget = Math.min(target.windowY, Math.max(0, maxWindow));

      if (el && maxInner > 0) {
        el.scrollTop = innerTarget;
      }
      if (windowTarget > 0) {
        window.scrollTo({ top: windowTarget, behavior: 'auto' });
      }

      // If we couldn't reach the saved position because layout is still
      // growing (images loading in), try again on the next frame — up
      // to ~30 frames (~0.5s).
      const reachedInner =
        !el || maxInner === 0 || el.scrollTop >= innerTarget - 1;
      const reachedWindow =
        windowTarget === 0 || window.scrollY >= windowTarget - 1;
      if ((!reachedInner || !reachedWindow) && attempts < 30) {
        requestAnimationFrame(tryRestore);
      } else {
        restoredRef.current = true;
      }
    }
    requestAnimationFrame(tryRestore);
  }, [loading, items, visibleCount]);

  useEffect(() => {
    apiFetch<ItemFull[]>('/api/catalog/items')
      .then(setItems)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  // Distinct facet values pulled from the catalogue.
  // Sizes also collect "fits like" values so the filter offers every size a
  // customer might want to filter by, not just label sizes.
  const facets = useMemo(() => {
    const brandSet = new Map<string, string>();
    const categorySet = new Map<string, string>();
    const occasionSet = new Map<string, string>();
    const sizeSet = new Set<string>();
    const conditionSet = new Set<string>();
    for (const item of items) {
      if (item.brand) brandSet.set(item.brand.id, item.brand.name);
      if (item.category) categorySet.set(item.category.id, item.category.name);
      for (const occ of item.occasions ?? []) {
        if (occ?.id) occasionSet.set(occ.id, occ.name);
      }
      if (item.label_size) sizeSet.add(item.label_size);
      if (item.recommended_size) sizeSet.add(item.recommended_size);
      if (item.condition) conditionSet.add(item.condition);
    }
    return {
      brands: [...brandSet.entries()]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        ),
      categories: [...categorySet.entries()]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        ),
      occasions: [...occasionSet.entries()]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        ),
      sizes: [...sizeSet].sort((a, b) => sizeRank(a) - sizeRank(b)),
      conditions: [...conditionSet].sort(
        (a, b) => conditionRank(a) - conditionRank(b)
      )
    };
  }, [items]);

  const filtered = useMemo(() => {
    let list = items;
    if (filters.brands.size) {
      list = list.filter((it) => it.brand && filters.brands.has(it.brand.id));
    }
    if (filters.categories.size) {
      list = list.filter(
        (it) => it.category && filters.categories.has(it.category.id)
      );
    }
    // Occasion filter — item passes if any of its occasions is selected.
    if (filters.occasions.size) {
      list = list.filter((it) =>
        (it.occasions ?? []).some((occ) => occ?.id && filters.occasions.has(occ.id))
      );
    }
    // Size filter matches EITHER the label size OR the "fits like" size, so a
    // M-labelled jacket that fits like an L will show up when filtering "L".
    if (filters.sizes.size) {
      list = list.filter(
        (it) =>
          (it.label_size && filters.sizes.has(it.label_size)) ||
          (it.recommended_size && filters.sizes.has(it.recommended_size))
      );
    }
    if (filters.conditions.size) {
      list = list.filter((it) => filters.conditions.has(it.condition));
    }
    list = [...list];
    if (filters.sort === 'price-asc' || filters.sort === 'price-desc') {
      // Price sorts still pin featured items to the top — featured rows
      // sort among themselves, then everything else.
      const direction = filters.sort === 'price-asc' ? 1 : -1;
      list.sort((a, b) => {
        const af = a.featured_at ? 1 : 0;
        const bf = b.featured_at ? 1 : 0;
        if (af !== bf) return bf - af; // featured first
        return (a.credit_price - b.credit_price) * direction;
      });
    }
    // For 'newest', trust the server order (featured first, then created_at DESC).
    return list;
  }, [items, filters]);

  const activeFilterCount =
    filters.brands.size +
    filters.categories.size +
    filters.occasions.size +
    filters.sizes.size +
    filters.conditions.size;

  const toggle = (
    key: 'brands' | 'categories' | 'occasions' | 'sizes' | 'conditions',
    value: string
  ) => {
    // User explicitly changed filters — abandon any pending scroll restore
    // and start over at page 1.
    restoredRef.current = true;
    setVisibleCount(PAGE_SIZE);
    setFilters((f) => {
      const next = new Set(f[key]);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...f, [key]: next };
    });
  };

  const clearAll = () => {
    restoredRef.current = true;
    setVisibleCount(PAGE_SIZE);
    setFilters(initialFilters());
  };

  return (
    <section className="screen">
      <div className="scroll browse-scroll" ref={scrollRef}>
        <div className="browse-head">
          <h1>BROWSE</h1>
          <div className="p">Everyday essentials to premium pieces.</div>
        </div>

        <div className="filters">
          <button
            className={`chip${activeFilterCount === 0 ? ' active' : ''}`}
            onClick={clearAll}
          >
            ALL
          </button>
          <button
            className="chip-icon"
            aria-label="Filters"
            onClick={() => setSheetOpen(true)}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <path d="M4 6h13M4 12h9M4 18h6" />
              <circle cx="19" cy="6" r="1.5" />
              <circle cx="15" cy="12" r="1.5" />
              <circle cx="12" cy="18" r="1.5" />
            </svg>
            {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
          </button>
        </div>

        {activeFilterCount > 0 && (
          <div className="active-filters">
            {[...filters.brands].map((id) => {
              const brand = facets.brands.find((b) => b.id === id);
              return (
                <span className="pill" key={`b-${id}`}>
                  {brand?.name ?? id}
                  <button className="x" onClick={() => toggle('brands', id)} aria-label="Remove">
                    <X />
                  </button>
                </span>
              );
            })}
            {[...filters.categories].map((id) => {
              const cat = facets.categories.find((c) => c.id === id);
              return (
                <span className="pill" key={`cat-${id}`}>
                  {cat?.name ?? id}
                  <button className="x" onClick={() => toggle('categories', id)} aria-label="Remove">
                    <X />
                  </button>
                </span>
              );
            })}
            {[...filters.occasions].map((id) => {
              const occ = facets.occasions.find((o) => o.id === id);
              return (
                <span className="pill" key={`occ-${id}`}>
                  {occ?.name ?? id}
                  <button className="x" onClick={() => toggle('occasions', id)} aria-label="Remove">
                    <X />
                  </button>
                </span>
              );
            })}
            {[...filters.sizes].map((s) => (
              <span className="pill" key={`s-${s}`}>
                {s}
                <button className="x" onClick={() => toggle('sizes', s)} aria-label="Remove">
                  <X />
                </button>
              </span>
            ))}
            {[...filters.conditions].map((c) => (
              <span className="pill" key={`c-${c}`}>
                {labelCondition(c)}
                <button className="x" onClick={() => toggle('conditions', c)} aria-label="Remove">
                  <X />
                </button>
              </span>
            ))}
            <button className="clear-all" onClick={clearAll}>
              CLEAR ALL
            </button>
          </div>
        )}

        <div className="browse-meta">
          <div className="ttl">THE ROTATION</div>
          <div className="count">
            {loading ? 'LOADING…' : `${filtered.length} ${filtered.length === 1 ? 'ITEM' : 'ITEMS'}`}
          </div>
        </div>

        {error && (
          <div className="empty-grid">
            <div className="h">Couldn&apos;t load the rotation</div>
            <div className="p">{error}</div>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="empty-grid">
            <div className="h">Nothing in this slice</div>
            <div className="p">
              {items.length === 0
                ? 'No pieces listed yet — check back soon.'
                : 'Try clearing some filters to widen your search.'}
            </div>
            {activeFilterCount > 0 && (
              <button
                className="chip"
                onClick={clearAll}
                style={{ background: 'var(--ink)', color: '#fff' }}
              >
                CLEAR FILTERS
              </button>
            )}
          </div>
        )}

        <div className="grid">
          {filtered.slice(0, visibleCount).map((item, idx) => {
            // Browse always shows the main image only — gallery images are for
            // the item detail page. If no main image exists, we render an empty
            // placeholder rather than fall back to a gallery shot.
            const img = item.main_image_url;
            // First page eagerly loads; later pages lazy-load. Browsers cache
            // them automatically based on the Storage CDN headers.
            const loading: 'eager' | 'lazy' = idx < 8 ? 'eager' : 'lazy';
            return (
              <Link
                to={`/browse/${item.slug}`}
                key={item.id}
                className="product-card"
                // backgroundLocation tells App.tsx to render the item as
                // a modal overlay on top of Browse, keeping this page
                // mounted (and its scroll position untouched).
                state={{ backgroundLocation: location }}
                onClick={() => {
                  // Belt-and-braces snapshot in case the customer
                  // navigates directly (e.g. shares the URL) — the
                  // saved state is then ready for them on back.
                  saveScrollState({
                    scrollTop: scrollRef.current?.scrollTop ?? 0,
                    windowY: window.scrollY,
                    visibleCount
                  });
                }}
              >
                <div className="img">
                  {img && (
                    <img
                      src={img}
                      alt={item.name}
                      loading={loading}
                      decoding="async"
                    />
                  )}
                  
                </div>
                <div className="brand-name">{item.brand?.name ?? '—'}</div>
                <div className="item-name">{item.name}</div>
                {(item.label_size || item.recommended_size) && (
                  <div className="sizes-row">
                    {item.label_size && (
                      <span className="rec">LABEL {item.label_size}</span>
                    )}
                    {item.recommended_size && (
                      <span className="rec">FITS LIKE {item.recommended_size}</span>
                    )}
                  </div>
                )}
                <div className="credits">
                  {item.credit_price}{' '}
                  {item.credit_price === 1 ? 'CREDIT' : 'CREDITS'}
                </div>
              </Link>
            );
          })}
        </div>

        {filtered.length > visibleCount && (
          <div style={{ textAlign: 'center', padding: '4px 16px 32px' }}>
            <button
              type="button"
              className="chip"
              onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
              style={{ background: 'var(--ink)', color: '#fff' }}
            >
              SHOW MORE ({filtered.length - visibleCount} LEFT)
            </button>
          </div>
        )}

        {!user && (
          <div className="footer-cta pad-bottom">
            <Link to="/sign-up" className="cta-full">
              <span>JOIN THE ROTATION</span>
              <Arrow />
            </Link>
          </div>
        )}
      </div>

      {/* Filter sheet */}
      <div className={`filter-sheet${sheetOpen ? ' open' : ''}`}>
        <div className="backdrop" onClick={() => setSheetOpen(false)} />
        <div className="sheet">
          <div className="grabber" />
          <div className="sheet-head">
            <h2>FILTERS</h2>
            <div className="right">
              {activeFilterCount > 0 && (
                <button className="clear" onClick={clearAll}>
                  Clear all
                </button>
              )}
              <button
                className="close"
                onClick={() => setSheetOpen(false)}
                aria-label="Close"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>
          </div>
          <div className="body">
            {facets.categories.length > 0 && (
              <div className="filter-section">
                <h3>ITEM TYPE</h3>
                <div className="opts">
                  {facets.categories.map((c) => (
                    <button
                      key={c.id}
                      className={`opt${filters.categories.has(c.id) ? ' selected' : ''}`}
                      onClick={() => toggle('categories', c.id)}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {facets.brands.length > 0 && (
              <div className="filter-section">
                <h3>BRAND</h3>
                <div className="opts">
                  {facets.brands.map((b) => (
                    <button
                      key={b.id}
                      className={`opt${filters.brands.has(b.id) ? ' selected' : ''}`}
                      onClick={() => toggle('brands', b.id)}
                    >
                      {b.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {facets.occasions.length > 0 && (
              <div className="filter-section">
                <h3>OCCASION</h3>
                <div className="opts">
                  {facets.occasions.map((o) => (
                    <button
                      key={o.id}
                      className={`opt${filters.occasions.has(o.id) ? ' selected' : ''}`}
                      onClick={() => toggle('occasions', o.id)}
                    >
                      {o.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {facets.sizes.length > 0 && (
              <div className="filter-section">
                <h3>SIZE</h3>
                <div className="opts">
                  {facets.sizes.map((s) => (
                    <button
                      key={s}
                      className={`opt${filters.sizes.has(s) ? ' selected' : ''}`}
                      onClick={() => toggle('sizes', s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {facets.conditions.length > 0 && (
              <div className="filter-section">
                <h3>CONDITION</h3>
                <div className="opts">
                  {facets.conditions.map((c) => (
                    <button
                      key={c}
                      className={`opt${filters.conditions.has(c) ? ' selected' : ''}`}
                      onClick={() => toggle('conditions', c)}
                    >
                      {labelCondition(c)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="filter-section">
              <h3>SORT BY</h3>
              <div className="opts">
                {(
                  [
                    ['newest', 'Newest'],
                    ['price-asc', 'Credits: low to high'],
                    ['price-desc', 'Credits: high to low']
                  ] as [SortKey, string][]
                ).map(([key, label]) => (
                  <button
                    key={key}
                    className={`opt${filters.sort === key ? ' selected' : ''}`}
                    onClick={() => setFilters((f) => ({ ...f, sort: key }))}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="sheet-foot">
            <button className="show-btn" onClick={() => setSheetOpen(false)}>
              SHOW {filtered.length} {filtered.length === 1 ? 'ITEM' : 'ITEMS'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function labelCondition(c: string): string {
  return c
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function X() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function Arrow() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}
